// HR-side document list for a specific employee record (the self-service view
// lives in features/pages/employee/EmployeeDocuments.tsx). Simple list + upload
// + delete against the employee-service documents API.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { FileText, Upload, Loader2, Trash2, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import { getDocuments, uploadDocument, deleteDocument } from '@/lib/hrApi';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import { getApiErrorMessage } from '@/lib/apiError';

interface DocRow {
  id: string;
  name: string;
  type: string;
  date: string;
  url?: string;
}

const DOC_TYPES = ['id', 'passport', 'driver_license', 'degree', 'certificate', 'contract', 'other'] as const;

const mapDocs = (docs: any[]): DocRow[] =>
  (Array.isArray(docs) ? docs : []).map((d: any, idx: number) => ({
    id: String(d.id ?? idx),
    name: d.name || d.file_name || d.title || 'Document',
    type: d.document_type || d.type || 'other',
    date: (d.uploaded_at || d.created_at || d.date || '').slice(0, 10),
    url: d.download_url || d.url || d.file_url,
  }));

export const EmployeeDocumentsCard: React.FC<{
  employeeId: string;
  canManage: boolean;
  /** Fired after any successful upload/delete so siblings (e.g. the
   *  required-documents checklist) can refetch. */
  onChanged?: () => void;
}> = ({ employeeId, canManage, onChanged }) => {
  const { t } = useTranslation('employees');
  const confirm = useConfirm();
  const [docs, setDocs] = useState<DocRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState<string>('other');
  const [error, setError] = useState<string | null>(null);

  const reload = () =>
    getDocuments(employeeId)
      .then((d) => { setDocs(mapDocs(d)); setError(null); })
      .catch((e: any) => setError(e?.response?.data?.detail || t('documents.load_error', { defaultValue: 'Could not load documents.' })));

  useEffect(() => {
    setLoading(true);
    reload().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  const onUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      await uploadDocument(employeeId, file, docType);
      await reload();
      onChanged?.();
      toast.success(t('documents.uploaded', { defaultValue: 'Document uploaded.' }));
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, t('documents.upload_failed', { defaultValue: 'Could not upload document.' })));
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (doc: DocRow) => {
    const ok = await confirm({
      message: t('documents.delete_confirm', { defaultValue: 'Delete "{{name}}"? This cannot be undone.', name: doc.name }),
      danger: true,
      confirmLabel: t('documents.delete', { defaultValue: 'Delete' }),
    });
    if (!ok) return;
    try {
      await deleteDocument(employeeId, doc.id);
      setDocs((d) => d.filter((x) => x.id !== doc.id));
      onChanged?.();
      toast.success(t('documents.deleted', { defaultValue: 'Document deleted.' }));
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, t('documents.delete_failed', { defaultValue: 'Could not delete document.' })));
    }
  };

  const typeLabel: Record<string, string> = {
    id: t('documents.type_id', { defaultValue: 'National ID' }),
    passport: t('documents.type_passport', { defaultValue: 'Passport' }),
    driver_license: t('documents.type_driver_license', { defaultValue: 'Driver license' }),
    degree: t('documents.type_degree', { defaultValue: 'Degree' }),
    certificate: t('documents.type_certificate', { defaultValue: 'Certificate' }),
    contract: t('documents.type_contract', { defaultValue: 'Contract' }),
    other: t('documents.type_other', { defaultValue: 'Other' }),
  };

  return (
    <Card className="border-orange-100 dark:border-orange-500/20">
      <CardHeader>
        <CardTitle className="text-base text-orange-700 flex items-center gap-2">
          <FileText className="w-4 h-4" /> {t('documents.title', { defaultValue: 'Documents' })}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {canManage && (
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              aria-label={t('documents.type', { defaultValue: 'Document type' })}
              className="h-10 px-3 rounded-md border border-border bg-card text-sm"
            >
              {DOC_TYPES.map((dt) => <option key={dt} value={dt}>{typeLabel[dt]}</option>)}
            </select>
            <label className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium cursor-pointer">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading ? t('documents.uploading', { defaultValue: 'Uploading…' }) : t('documents.upload', { defaultValue: 'Upload document' })}
              <input type="file" className="hidden" onChange={onUpload} disabled={uploading}
                aria-label={t('documents.upload', { defaultValue: 'Upload document' })} />
            </label>
          </div>
        )}

        {error && <p className="text-sm text-danger">{error}</p>}

        {loading ? (
          <div className="py-6 flex items-center justify-center text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> {t('documents.loading', { defaultValue: 'Loading documents…' })}
          </div>
        ) : docs.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            {t('documents.empty', { defaultValue: 'No documents on this record yet.' })}
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {docs.map((d) => (
              <li key={d.id} className="flex items-center gap-3 py-2.5">
                <FileText className="w-4 h-4 text-orange-500 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-foreground truncate">{d.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {typeLabel[d.type] || d.type}{d.date ? ` · ${d.date}` : ''}
                  </p>
                </div>
                {d.url && (
                  <Button variant="outline" size="sm" onClick={() => window.open(d.url, '_blank', 'noopener')}>
                    <Download className="w-4 h-4" />
                  </Button>
                )}
                {canManage && (
                  <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={() => onDelete(d)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

export default EmployeeDocumentsCard;
